import { useEffect, useMemo, useState } from 'react';

import isFinite from 'lodash/isFinite';

import { validateLeftArea, validateRightArea } from './utils';
import { RefAreaValue, ZoomProps } from '../../types';

type UseChartSyncArgs<T> = {
  chartData: T[];
  syncId?: string | number;
  onSyncZoom?: (from: RefAreaValue, to: RefAreaValue) => void;
} & Pick<ZoomProps, 'minValue' | 'maxValue' | 'xAxisDataKey'>;

export const useChartSync = <T>({
  syncId,
  onSyncZoom,
  minValue,
  maxValue,
  xAxisDataKey,
  chartData,
}: UseChartSyncArgs<T>) => {
  const [refAreaLeft, setRefAreaLeft] = useState<RefAreaValue>(null);
  const [refAreaRight, setRefAreaRight] = useState<RefAreaValue>(null);

  const dataMapByXAxisKey = useMemo(
    () =>
      new Map(chartData.map((d, index) => [d[xAxisDataKey as keyof T], index])),
    [chartData, xAxisDataKey]
  );

  const syncLeftArea = (value: RefAreaValue) => {
    const safeValue = validateLeftArea({
      dataMapByXAxisKey,
      value,
      minValue,
      maxValue,
    });

    if (value !== refAreaLeft && syncId) {
      window.postMessage(
        { type: `SYNC_CHART_${syncId}_LEFT`, payload: safeValue },
        window.location.origin
      );
    } else {
      setRefAreaLeft(safeValue);
    }
  };

  const syncRightArea = (value: RefAreaValue) => {
    const safeValue = validateRightArea({
      dataMapByXAxisKey,
      value,
      minValue,
      maxValue,
      leftAreaValue: refAreaLeft,
    });

    if (value !== refAreaRight && syncId) {
      window.postMessage(
        { type: `SYNC_CHART_${syncId}_RIGHT`, payload: safeValue },
        window.location.origin
      );
    } else {
      setRefAreaRight(safeValue);
    }
  };

  const syncZoom = (from: RefAreaValue, to: RefAreaValue) => {
    if (syncId) {
      const safeFrom =
        typeof from === 'number' || typeof from === 'string' || from === null
          ? from
          : null;
      const safeTo =
        typeof to === 'number' || typeof to === 'string' || to === null
          ? to
          : null;

      window.postMessage(
        {
          type: `SYNC_CHART_${syncId}_ZOOM`,
          payload: { from: safeFrom, to: safeTo },
        },
        window.location.origin
      );
    } else {
      onSyncZoom?.(from, to);
    }
  };

  useEffect(() => {
    if (syncId) {
      const handler = (event: MessageEvent) => {
        if (event.data?.type === `SYNC_CHART_${syncId}_ZOOM`) {
          const payload = event.data.payload;
          if (payload && typeof payload === 'object') {
            const { from, to } = payload;
            if (
              (isFinite(from) && isFinite(to)) ||
              (typeof from === 'string' && typeof to === 'string') ||
              (from === null && to === null)
            ) {
              onSyncZoom?.(from, to);
            }
          }
        }
      };
      window.addEventListener('message', handler);
      return () => window.removeEventListener('message', handler);
    }
    return undefined;
  }, [syncId, onSyncZoom]);

  useEffect(() => {
    if (syncId) {
      const handler = (event: MessageEvent) => {
        if (event.data?.type === `SYNC_CHART_${syncId}_LEFT`) {
          const payload = event.data.payload;
          if (
            isFinite(payload) ||
            typeof payload === 'string' ||
            payload === null
          ) {
            if (payload !== refAreaLeft) {
              setRefAreaLeft(payload);
            }
          }
        }
      };
      window.addEventListener('message', handler);
      return () => window.removeEventListener('message', handler);
    }
    return undefined;
  }, [syncId, refAreaLeft]);

  useEffect(() => {
    if (syncId) {
      const handler = (event: MessageEvent) => {
        if (event.data?.type === `SYNC_CHART_${syncId}_RIGHT`) {
          const payload = event.data.payload;
          if (
            isFinite(payload) ||
            typeof payload === 'string' ||
            payload === null
          ) {
            if (payload !== refAreaRight) {
              setRefAreaRight(payload);
            }
          }
        }
      };
      window.addEventListener('message', handler);
      return () => window.removeEventListener('message', handler);
    }
    return undefined;
  }, [syncId, refAreaRight]);

  return {
    refAreaLeft,
    setRefAreaLeft: syncLeftArea,
    refAreaRight,
    setRefAreaRight: syncRightArea,
    syncZoom,
  };
};
